import React, { useState, useEffect } from "react";

import {
  Flex,
  Box,
  Text,
} from "@chakra-ui/react";

import {
  EllipsisVertical,
  EyeIcon,
  Pencil,
} from "lucide-react";

import ReusableTable from "components/Tables/ReusableTable";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import StatusBadge, {
  getStatusVariant,
} from "components/Badges/StatusBadge";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { useDispatch, useSelector } from "react-redux";

import {
  fetchStarterKitPurchasesAction,
  fetchStarterKitPaymentProofsAction,
} from "../../../state/actions/starterKitPurchaseActions";

import ViewStarterKitPurchase from "./components/ViewStarterKitPurchase";
import UpdatePaymentProofDrawer from "./components/UpdatePaymentProof";

import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";

import { formatDate } from "utils/dateFormatter";


const StarterKitPurchaseManagement = () => {

  const dispatch = useDispatch();


  const {
    purchases,
    paymentProofs,
    purchasesTotal,
    loading,
    error,
    hasMore,
    total,
  } = useSelector(
    (state) => state.starterKitPurchases
  );


  const [
    isViewPurchaseDrawerOpen,
    setViewPurchaseDrawerOpen,
  ] = useState(false);


  const [
    isUpdatePaymentDrawerOpen,
    setUpdatePaymentDrawerOpen,
  ] = useState(false);


  const [
    selectedPurchase,
    setSelectedPurchase,
  ] = useState(null);


  const [
    selectedPaymentProof,
    setSelectedPaymentProof,
  ] = useState(null);


  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);


  const PAGE_SIZE = 20;


  const [
    searchField,
    setSearchField,
  ] = useState(null);


  const [
    searchValue,
    setSearchValue,
  ] = useState("");


  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");


  /* =========================
     SEARCH
  ========================= */

  useEffect(() => {

    const timer = setTimeout(() => {

      setSearchValue(
        searchTerm
      );

      setCurrentPage(1);

    }, 500);

    return () =>
      clearTimeout(timer);

  }, [searchTerm]);


  /* =========================
     FETCH PURCHASES
  ========================= */

  useEffect(() => {

    dispatch(
      fetchStarterKitPurchasesAction(
        currentPage,
        PAGE_SIZE,
        searchField,
        searchValue
      )
    );

  }, [
    dispatch,
    currentPage,
    searchField,
    searchValue,
  ]);


  /* =========================
     FETCH PAYMENT PROOFS
  ========================= */

  useEffect(() => {

    dispatch(
      fetchStarterKitPaymentProofsAction(
        1,
        100
      )
    );

  }, [dispatch]);


  /* =========================
     SEARCH HANDLER
  ========================= */

  const handleSearch = (
    field,
    value
  ) => {

    setCurrentPage(1);

    setSearchField(field);

    setSearchTerm(value);

  };


  /* =========================
     VIEW PURCHASE
  ========================= */

  const handleViewPurchase = (
    purchase
  ) => {

    setSelectedPurchase(
      purchase
    );

    setViewPurchaseDrawerOpen(
      true
    );

  };


  const handleCloseViewPurchaseDrawer =
    () => {

      setSelectedPurchase(null);

      setViewPurchaseDrawerOpen(
        false
      );

    };


  /* =========================
     UPDATE PAYMENT PROOF
  ========================= */

  const handleUpdatePaymentProof = (
    purchase
  ) => {

    const proofs =
      Array.isArray(
        paymentProofs?.data
      )
        ? paymentProofs.data
        : Array.isArray(
            paymentProofs
          )
          ? paymentProofs
          : [];


    const proof =
      proofs.find(
        (item) =>
          item.starter_kit_purchase_id ===
          purchase.starter_kit_purchase_id
      );


    setSelectedPurchase(
      purchase
    );

    setSelectedPaymentProof(
      proof || null
    );

    setUpdatePaymentDrawerOpen(
      true
    );

  };


  const handleCloseUpdatePaymentDrawer =
    () => {

      setSelectedPurchase(null);

      setSelectedPaymentProof(
        null
      );

      setUpdatePaymentDrawerOpen(
        false
      );

    };


  /* =========================
     ACTION MENU
  ========================= */

  const generateMenuItems = (
    row
  ) => {

    const purchase =
      row.original;


    return [

      {
        icon:
          <EyeIcon
            size={16}
          />,

        text:
          "View Purchase",

        onClick: () =>
          handleViewPurchase(
            purchase
          ),
      },


      {
        icon:
          <Pencil
            size={16}
          />,

        text:
          "Update Payment Status",

        onClick: () =>
          handleUpdatePaymentProof(
            purchase
          ),
      },

    ];

  };


  /* =========================
     TABLE COLUMNS
  ========================= */

  const columnsData = [

    {
      Header:
        "Purchase Number",

      accessor:
        (row) =>
          row.purchase_number ||
          "N/A",
    },


    {
      Header:
        "Agent Name",

      accessor:
        (row) => {

          const firstName =
            row.agent?.user
              ?.first_name ||
            "";

          const lastName =
            row.agent?.user
              ?.last_name ||
            "";


          return (
            `${firstName} ${lastName}`.trim() ||
            row.agent?.user?.username ||
            "N/A"
          );

        },
    },


    {
      Header:
        "Agent Number",

      accessor:
        (row) =>
          row.agent?.agent_number ||
          "N/A",
    },


    {
      Header:
        "Items",

      accessor:
        (row) => {

          if (
            !Array.isArray(
              row.items
            )
          ) {
            return 0;
          }


          return row.items.reduce(
            (
              total,
              item
            ) =>
              total +
              Number(
                item.quantity || 0
              ),
            0
          );

        },
    },


    {
      Header:
        "Total Amount",

      accessor:
        (row) =>
          `P ${Number(
            row.total_amount || 0
          ).toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`,
    },


    {
      Header:
        "Purchase Status",

      accessor:
        (row) =>
          row.purchase_status ||
          "Unknown",

      Cell:
        ({ value }) => (

          <StatusBadge
            label={value}
            variant={
              getStatusVariant(
                value
              )
            }
          />

        ),
    },


    {
      Header:
        "Payment Status",

      accessor:
        (row) => {

          const proofs =
            Array.isArray(
              paymentProofs?.data
            )
              ? paymentProofs.data
              : Array.isArray(
                  paymentProofs
                )
                ? paymentProofs
                : [];


          const proof =
            proofs.find(
              (item) =>
                item.starter_kit_purchase_id ===
                row.starter_kit_purchase_id
            );


          return (
            proof?.status ||
            "No Proof"
          );

        },

      Cell:
        ({ value }) => (

          <StatusBadge
            label={value}
            variant={
              value === "Verified"
                ? "success"
                : value === "Rejected"
                  ? "error"
                  : value === "Pending"
                    ? "warning"
                    : "gray"
            }
          />

        ),
    },


    {
      Header:
        "Created",

      accessor:
        (row) =>
          row.created_at,

      Cell:
        ({ value }) =>
          value
            ? formatDate(value)
            : "N/A",
    },


    {
      Header:
        "Actions",

      accessor:
        "moreOptions",

      Cell:
        ({ row }) => (

          <ThreeDotMenu

            icon={
              <EllipsisVertical
                size={20}
                color="grey"
              />
            }

            items={
              generateMenuItems(
                row
              )
            }

          />

        ),
    },

  ];


  /* =========================
     RENDER
  ========================= */

  return (

    <Flex
      flexDirection="column"
      pt={{
        base: "120px",
        md: "25px",
      }}
    >

      {/* HEADER */}

      <Box p={6}>

        <Flex
          justifyContent="space-between"
          alignItems="center"
        >

          <Text
            fontSize="md"
            fontWeight="bold"
          >

            {loading ? (

              <Skeleton
                height="20px"
                width="180px"
              />

            ) : (

            `All Starter Kit Purchases: ${purchasesTotal ?? 0}`

            )}

          </Text>

        </Flex>

      </Box>


      {/* TABLE */}

      <ReusableTable

        columnsData={
          columnsData
        }

        tableData={
          Array.isArray(
            purchases?.data
          )
            ? purchases.data
            : []
        }

        isLoading={
          loading
        }

        error={
          error
        }

        onSearch={
          handleSearch
        }

        emptyMessage={
          "No starter kit purchases found. Try adjusting your search or filters."
        }

      />


      {/* PAGINATION */}

      <Box
        mt={4}
        display="flex"
        justifyContent="center"
        gap={2}
      >

        <GradientButton

          onClick={() =>
            setCurrentPage(
              (prev) =>
                Math.max(
                  prev - 1,
                  1
                )
            )
          }

          disabled={
            currentPage === 1
          }

          height="30px"
        >
          Previous
        </GradientButton>


        <Text
          alignSelf="center"
        >
          Page {currentPage}
        </Text>


        <GradientButton

          onClick={() =>
            setCurrentPage(
              (prev) =>
                prev + 1
            )
          }

          disabled={
            !hasMore
          }

          height="30px"
        >
          Next
        </GradientButton>

      </Box>


      {/* VIEW PURCHASE DRAWER */}

      <ReusableDrawer

        isOpen={
          isViewPurchaseDrawerOpen
        }

        onClose={
          handleCloseViewPurchaseDrawer
        }

        headerContent={
          <h2>
            View Starter Kit Purchase
          </h2>
        }

        bodyContent={

          selectedPurchase && (

            <ViewStarterKitPurchase
              purchase={
                selectedPurchase
              }

              onClose={
                handleCloseViewPurchaseDrawer
              }
            />

          )

        }

      />


      {/* UPDATE PAYMENT PROOF DRAWER */}

      <UpdatePaymentProofDrawer

        isOpen={
          isUpdatePaymentDrawerOpen
        }

        onClose={
          handleCloseUpdatePaymentDrawer
        }

        paymentProof={
          selectedPaymentProof
        }

      />

    </Flex>

  );

};


export default StarterKitPurchaseManagement;